/**
 * /settings/agim-skills — combined skill management.
 *
 * 2026-05 consolidation: the v2 SPA briefly had two skill entry points
 * (top-level /skills + this settings route) which duplicated 95% of
 * the data after multi-root scanning landed. We merged both flows here:
 *
 *   installed — agim's own loader view; CRUD against the writable
 *               workspace dir, read-only for builtins and skills
 *               inherited from ~/.claude/skills + opencode + codex.
 *
 * Old URLs under /skills still resolve via redirects in router.tsx
 * so bookmarks keep working.
 */

import { NavLink, Outlet, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { useIsAdmin } from '@/stores/auth'
import { NoPermissionPanel } from '@/components/shell/no-permission'

interface SubTab {
  to: string
  i18nKey: string
  end?: boolean
  adminOnly?: boolean
}

const SUB_TABS: SubTab[] = [
  { to: '.', i18nKey: 'agimSkills.tab.overview', end: true },
  { to: 'installed', i18nKey: 'agimSkills.tab.installed' },
  { to: 'injection', i18nKey: 'agimSkills.tab.injection', adminOnly: true },
]

export default function SettingsAgimSkillsLayout(): JSX.Element {
  const { t } = useTranslation(['settings'])
  const isAdmin = useIsAdmin()
  const location = useLocation()
  const onInjection = /\/agim-skills\/injection\/?$/.test(location.pathname)
  const tabs = SUB_TABS.filter((tab) => isAdmin || !tab.adminOnly)
  if (!isAdmin && onInjection) {
    return <NoPermissionPanel />
  }
  return (
    <div className="flex flex-col gap-4">
      <nav
        className={cn(
          'flex items-center gap-1',
          'border-b border-border',
          '-mx-3 sm:-mx-4 px-3 sm:px-4 pb-2',
          'overflow-x-auto',
        )}
        aria-label={t('agimSkills.title')}
      >
        {tabs.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            end={tab.end}
            className={({ isActive }) =>
              cn(
                'shrink-0 rounded-md px-3 py-1.5 text-sm no-underline',
                'transition-colors tap-target',
                isActive
                  ? 'bg-accent-bg text-accent font-medium'
                  : 'text-text-dim hover:bg-surface-hover hover:text-text',
              )
            }
          >
            {t(tab.i18nKey)}
          </NavLink>
        ))}
      </nav>
      <Outlet />
    </div>
  )
}
